import React, { useState } from "react";
import { toast } from "react-toastify";
import {
  ButtonPrimary,
  ButtonSecondary,
  HStack,
  VStack,
} from "../../../../components/utils";
import { bulkApprovePaymentStatus } from "../../../../api/TransactionApisV2";
import {
  EmployeeOfferDetails,
  PaymentDetails,
} from "../../../../types/Transaction";

type Target = { employee: EmployeeOfferDetails; payment: PaymentDetails };
type Override = {
  referenceId: string;
  paymentDate: string;
  dematAccountId: string;
  dematType: "NSDL" | "CDSL";
};

const paymentStatusConfig: Record<
  string,
  { label: string; className: string }
> = {
  PAYMENT_PENDING: {
    label: "Pending Approval",
    className: "bg-yellow-100 text-yellow-800 border border-yellow-200",
  },
  SUCCESS: {
    label: "Approved",
    className: "bg-green-100 text-green-800 border border-green-200",
  },
  FAILED: {
    label: "Rejected",
    className: "bg-red-100 text-red-800 border border-red-200",
  },
};

const StatusBadge = ({ status }: { status?: string }) => {
  if (!status) return null;
  const config = paymentStatusConfig[status] || {
    label: status,
    className: "bg-gray-100 text-gray-700",
  };
  return (
    <span
      className={`px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${config.className}`}
    >
      {config.label}
    </span>
  );
};

const formatDateDisplay = (date: string | Date | undefined) => {
  if (!date) return "—";
  const parsedDate = new Date(date);
  if (Number.isNaN(parsedDate.getTime())) return String(date);
  return parsedDate.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export default function BulkApprovePaymentDetails({
  transactionId,
  paymentType,
  targets,
  onClose,
  onSuccess,
}: {
  transactionId: string;
  paymentType: "exercise price" | "tax price";
  targets: Target[];
  onClose: () => void;
  onSuccess: (data: any) => void;
}) {
  const [savingAction, setSavingAction] = useState<"approve" | "reject" | null>(
    null
  );
  const [overrides, setOverrides] = useState<Record<string, Override>>(() =>
    Object.fromEntries(
      targets.map(({ employee, payment }) => [
        payment.id!,
        {
          referenceId: payment.referenceId || "",
          paymentDate: payment.paymentDate
            ? new Date(payment.paymentDate).toISOString().slice(0, 10)
            : "",
          dematAccountId:
            (employee.monetaryTransactionDetails as any)?.offerMetaData
              ?.dematAccountDetails?.dematAccountId || "",
          dematType:
            (employee.monetaryTransactionDetails as any)?.offerMetaData
              ?.dematAccountDetails?.type || "NSDL",
        },
      ])
    )
  );
  const financed = (payment: PaymentDetails) =>
    payment.metaData?.financingType === "ESOP_FINANCING";
  const update = (id: string, value: Partial<Override>) =>
    setOverrides((previous) => ({
      ...previous,
      [id]: { ...previous[id], ...value },
    }));
  const submit = async (action: "approve" | "reject") => {
    setSavingAction(action);
    try {
      const response = await bulkApprovePaymentStatus({
        transactionId,
        paymentType,
        status: action === "approve" ? "SUCCESS" : "FAILED",
        approvals: targets.map(({ employee, payment }) => ({
          paymentDetailId: payment.id!,
          employeeId: employee.employeeId,
          requestId: employee.requestId!,
          ...(action === "approve" && financed(payment)
            ? {
                paymentOverride: {
                  referenceId: overrides[payment.id!]?.referenceId,
                  paymentDate: overrides[payment.id!]?.paymentDate,
                },
                ...(overrides[payment.id!]?.dematAccountId
                  ? {
                      dematOverride: {
                        dematAccountId: overrides[payment.id!].dematAccountId,
                        type: overrides[payment.id!].dematType,
                      },
                    }
                  : {}),
              }
            : {}),
        })),
      });
      toast.success(
        `Payment tranches ${
          action === "approve" ? "approved" : "rejected"
        } successfully.`
      );
      onSuccess(response.data);
      onClose();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.errorRaw ||
          error?.response?.data?.message ||
          error?.message ||
          `Unable to ${action} payment tranches.`
      );
    } finally {
      setSavingAction(null);
    }
  };
  return (
    <div className="w-[1100px] max-w-full mx-auto bg-white rounded-lg shadow-sm">
      <div className="flex items-center justify-between px-6 py-6 border-b border-gray-200">
        <div>
          <h6 className="text-lg font-semibold text-gray-900">
            Review{" "}
            {paymentType === "exercise price" ? "Exercise Price" : "Tax Price"}{" "}
            Payments
          </h6>
          <p className="mt-1 text-sm text-gray-500">
            {targets.length} pending payment{targets.length === 1 ? "" : "s"}{" "}
            selected
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          disabled={Boolean(savingAction)}
          className="text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary/20 rounded p-1 disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Close bulk payment review"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
      <VStack className="gap-6 p-6">
        <p className="text-sm text-gray-500">
          Review all selected pending tranches, then approve or reject them.
        </p>
        <div className="max-h-[420px] overflow-auto border border-gray-200 rounded-lg shadow-sm">
          <table className="w-full min-w-[1050px] text-sm text-left">
            <thead className="sticky top-0 z-10 bg-gray-50 border-b border-gray-200 text-xs text-gray-500 uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 font-medium">#</th>
                <th className="px-4 py-3 font-medium">Employee</th>
                <th className="px-4 py-3 font-medium">Request</th>
                <th className="px-4 py-3 font-medium">Reference ID</th>
                <th className="px-4 py-3 font-medium">Date</th>
                <th className="px-4 py-3 text-right font-medium">Amount</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Financing</th>
                <th className="px-4 py-3 font-medium">Proof</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {targets.map(({ employee, payment }, index) => (
                <React.Fragment key={payment.id || index}>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-500">{index + 1}</td>
                    <td className="px-4 py-3">
                      <span className="font-medium text-gray-900">
                        {employee.employeeName || employee.employeeId}
                      </span>
                      <span className="block text-xs text-gray-500">
                        {employee.hrId}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {employee.requestId || "—"}
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-900">
                      {payment.referenceId || "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {formatDateDisplay(payment.paymentDate)}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-900">
                      ₹{(payment.amount ?? 0).toLocaleString("en-IN")}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={payment.paymentStatus} />
                    </td>
                    <td className="px-4 py-3">
                      {financed(payment) ? (
                        <VStack className="gap-0.5">
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 w-fit">
                            ESOP Financing
                          </span>
                          {payment.metaData?.financierName && (
                            <span className="text-xs text-gray-500">
                              {payment.metaData.financierName}
                            </span>
                          )}
                        </VStack>
                      ) : (
                        <span className="text-xs text-gray-400">Self</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {payment.documentLinks?.length ? (
                        <div className="flex flex-col gap-1.5">
                          {payment.documentLinks.map(
                            (document, documentIndex) =>
                              document.s3Url ? (
                                <button
                                  key={document.id || documentIndex}
                                  type="button"
                                  className="text-primary hover:text-primaryHover hover:underline text-left truncate max-w-[150px] transition-colors focus:outline-none focus:ring-1 focus:ring-primary rounded px-1 -ml-1"
                                  onClick={() =>
                                    window.open(document.s3Url, "_blank")
                                  }
                                  title={document.name || "View"}
                                >
                                  {document.name || `File ${documentIndex + 1}`}
                                </button>
                              ) : null
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400 italic">—</span>
                      )}
                    </td>
                  </tr>
                  {financed(payment) && payment.id && (
                    <tr>
                      <td
                        colSpan={9}
                        className="px-4 py-3 bg-gray-50 border-t border-gray-100"
                      >
                        <VStack className="gap-3">
                          <span className="text-xs font-medium text-gray-700">
                            This payment was recorded via ESOP Financing.
                            Confirm the transaction details before approving it.
                          </span>
                          <HStack className="gap-2 items-center">
                            <input
                              type="text"
                              placeholder="Reference ID"
                              value={overrides[payment.id]?.referenceId || ""}
                              onChange={(event) =>
                                update(payment.id!, {
                                  referenceId: event.target.value,
                                })
                              }
                              className="px-2 py-1.5 border border-gray-200 rounded text-sm w-56 focus:outline-none focus:ring-1 focus:ring-primary"
                            />
                            <input
                              type="date"
                              value={overrides[payment.id]?.paymentDate || ""}
                              onChange={(event) =>
                                update(payment.id!, {
                                  paymentDate: event.target.value,
                                })
                              }
                              className="px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                            />
                            <input
                              type="text"
                              placeholder="Demat Account Number (optional)"
                              value={
                                overrides[payment.id]?.dematAccountId || ""
                              }
                              onChange={(event) =>
                                update(payment.id!, {
                                  dematAccountId: event.target.value,
                                })
                              }
                              className="px-2 py-1.5 border border-gray-200 rounded text-sm w-56 focus:outline-none focus:ring-1 focus:ring-primary"
                            />
                            <select
                              value={overrides[payment.id]?.dematType || "NSDL"}
                              onChange={(event) =>
                                update(payment.id!, {
                                  dematType: event.target
                                    .value as Override["dematType"],
                                })
                              }
                              className="px-2 py-1.5 border border-gray-200 rounded text-sm"
                            >
                              <option value="NSDL">NSDL</option>
                              <option value="CDSL">CDSL</option>
                            </select>
                          </HStack>
                        </VStack>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <HStack className="justify-between gap-3 pt-2 border-t border-gray-100">
          <ButtonSecondary disabled={Boolean(savingAction)} onClick={onClose}>
            Cancel
          </ButtonSecondary>
          <HStack className="justify-end gap-3">
            <ButtonSecondary
              type="button"
              disabled={Boolean(savingAction)}
              onClick={() => submit("reject")}
            >
              {savingAction === "reject"
                ? "Rejecting…"
                : `Reject ${targets.length} Payment${
                    targets.length === 1 ? "" : "s"
                  }`}
            </ButtonSecondary>
            <ButtonPrimary
              disabled={Boolean(savingAction)}
              onClick={() => submit("approve")}
            >
              {savingAction === "approve"
                ? "Approving…"
                : `Approve ${targets.length} Payment${
                    targets.length === 1 ? "" : "s"
                  }`}
            </ButtonPrimary>
          </HStack>
        </HStack>
      </VStack>
    </div>
  );
}
